import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, tokens } from '@shared/api';
import { idr, num } from '@shared/format';
import {
  Alert,
  Card,
  CopyButton,
  Icon,
  KeyValue,
  Modal,
  PageHeader,
  Segmented,
  setStoredTheme,
  useThemePreference,
  type ThemePreference,
} from '../components/ui';
import type { Session } from '../App';
import { BASE_URL } from '../snippets';
import '../styles/settings.css';

interface Me {
  memberships?: Array<{ organizationId: string; role: string }>;
}

interface Project {
  id: string;
  name: string;
  environment: string;
  status: string;
  active_keys: number;
  created_at: string;
}

interface Balance {
  available_balance: number;
  reserved_balance: number;
}

const ORG_ROLES: Record<string, string> = {
  OWNER: 'Pemilik',
  ADMIN: 'Admin',
  DEVELOPER: 'Developer',
  BILLING: 'Penagihan',
  VIEWER: 'Peninjau',
};

const PLATFORM_ROLES: Record<string, string> = {
  USER: 'Pengguna',
  SUPPORT: 'Tim dukungan',
  FINANCE: 'Tim keuangan',
  PLATFORM_ADMIN: 'Admin platform',
};

const THEMES: Array<{ id: ThemePreference; label: ReactNode }> = [
  {
    id: 'system',
    label: (
      <>
        <Icon name="monitor" size={15} />
        Sistem
      </>
    ),
  },
  {
    id: 'light',
    label: (
      <>
        <Icon name="sun" size={15} />
        Terang
      </>
    ),
  },
  {
    id: 'dark',
    label: (
      <>
        <Icon name="moon" size={15} />
        Gelap
      </>
    ),
  },
];

function Id({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className="settings-id">
      <code title={value}>{value}</code>
      <CopyButton value={value} className="btn-ghost" />
    </span>
  );
}

function usePrefersDark(): boolean {
  const [dark, setDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return dark;
}

export function Settings({ session }: { session: Session }) {
  const theme = useThemePreference();
  const prefersDark = usePrefersDark();
  const [role, setRole] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [params, setParams] = useSearchParams();
  const [deleting, setDeleting] = useState(params.get('hapus') === '1');

  // palette while already here
  useEffect(() => {
    if (params.get('hapus') === '1') setDeleting(true);
  }, [params]);

  useEffect(() => {
    let alive = true;
    void Promise.allSettled([
      api<Me>('/v1/auth/me'),
      api<{ data: Project[] }>('/v1/projects'),
      api<Balance>('/v1/wallet/balance'),
    ]).then(([me, list, wallet]) => {
      if (!alive) return;
      if (me.status === 'fulfilled') {
        const hit = me.value.memberships?.find((m) => m.organizationId === session.active_organization_id);
        setRole(hit?.role ?? null);
      }
      setProjects(list.status === 'fulfilled' ? list.value.data : []);
      if (wallet.status === 'fulfilled') setBalance(wallet.value);
    });
    return () => {
      alive = false;
    };
  }, [session.active_organization_id]);

  const signOut = () => {
    tokens.clear();
    window.location.assign('/');
  };

  const closeDelete = () => {
    setDeleting(false);
    if (params.has('hapus')) setParams({}, { replace: true });
  };

  const activeKeys = projects?.reduce((sum, p) => sum + (p.active_keys ?? 0), 0);
  const loading = <span className="muted">Memuat…</span>;

  const orgItems: Array<[ReactNode, ReactNode]> = [
    ['ID organisasi', <Id key="org" value={session.active_organization_id} />],
    ['Peran Anda', role ? ORG_ROLES[role] ?? role : projects ? <span className="muted">—</span> : loading],
    [
      'Proyek',
      projects ? (
        projects.length ? (
          <div className="settings-projects">
            {projects.map((project) => (
              <span key={project.id}>
                {project.name}
                <span className="tag">{project.environment === 'PRODUCTION' ? 'Production' : 'Development'}</span>
              </span>
            ))}
          </div>
        ) : (
          <span className="muted">Belum ada proyek</span>
        )
      ) : (
        loading
      ),
    ],
    ['API key aktif', activeKeys === undefined ? loading : <span className="tabular">{num(activeKeys)}</span>],
    [
      'Saldo tersedia',
      balance ? (
        <span className="tabular">
          {idr(balance.available_balance)}
          {balance.reserved_balance > 0 && <span className="muted"> · {idr(balance.reserved_balance)} ditahan</span>}
        </span>
      ) : (
        loading
      ),
    ],
    ['Laporan', 'Rupiah (IDR), zona waktu WIB'],
  ];

  return (
    <>
      <PageHeader title="Pengaturan" subtitle="Akun, organisasi, dan tampilan portal." />

      <div className="settings-grid">
        <div className="settings-col">
          <Card title="Akun">
            <KeyValue
              items={[
                ['Email', session.email],
                ['ID pengguna', <Id key="uid" value={session.user_id} />],
                ['Jenis akun', PLATFORM_ROLES[session.platform_role] ?? session.platform_role],
              ]}
            />
          </Card>

          <Card
            title="Organisasi"
            description="Satu saldo dipakai bersama oleh semua proyek dan API key."
            action={
              <Link to="/projects" className="btn btn-sm">
                Kelola proyek
              </Link>
            }
          >
            <KeyValue items={orgItems} />
          </Card>
        </div>

        <div className="settings-col">
          <Card
            title="Akses API"
            action={
              <Link to="/docs" className="btn btn-sm btn-ghost">
                Dokumentasi
                <Icon name="arrow" size={14} />
              </Link>
            }
          >
            <KeyValue
              items={[
                ['Base URL', <Id key="base" value={BASE_URL} />],
                ['Header', <code key="h">Authorization: Bearer aia_live_…</code>],
                [
                  'API key',
                  <Link key="k" to="/api-keys">
                    Buat atau cabut di menu API Key
                  </Link>,
                ],
              ]}
            />
          </Card>

          <Card title="Tampilan" description="Disimpan di browser ini saja.">
            <Segmented label="Tema" options={THEMES} value={theme} onChange={setStoredTheme} />
            <p className="settings-note">
              {theme === 'system'
                ? `Mengikuti pengaturan perangkat, saat ini ${prefersDark ? 'gelap' : 'terang'}.`
                : `Tema ${theme === 'dark' ? 'gelap' : 'terang'} dipakai walau perangkat berganti mode.`}
            </p>
          </Card>

          <Card title="Sesi" description="Keluar menghapus token login dari browser ini. API key yang sudah dibuat tetap berlaku.">
            <button className="btn btn-sm" onClick={signOut}>
              <Icon name="logout" size={15} />
              Keluar
            </button>
          </Card>

          <Card title="Hapus akun" description="Menutup akun dan organisasi Anda secara permanen.">
            <button className="btn btn-sm btn-danger" onClick={() => setDeleting(true)}>
              Hapus akun…
            </button>
          </Card>
        </div>
      </div>

      {deleting && <DeleteAccountModal balance={balance?.available_balance ?? null} onClose={closeDelete} />}
    </>
  );
}

const CONFIRM_WORD = 'HAPUS';

function DeleteAccountModal({ balance, onClose }: { balance: number | null; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = password.length > 0 && confirm.trim().toUpperCase() === CONFIRM_WORD;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await api('/v1/auth/me', { method: 'DELETE', body: { password } });
      tokens.clear();
      window.location.assign('/');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Hapus akun" onClose={onClose} width={480} dismissible={!busy}>
      {error && <Alert kind="error">{error}</Alert>}
      <form onSubmit={submit}>
        <ul className="settings-danger-list">
          <li>Semua API key langsung dicabut dan semua proyek dinonaktifkan.</li>
          {balance !== null && balance > 0 && (
            <li>
              Sisa saldo <strong className="tabular">{idr(balance)}</strong> hangus dan tidak bisa dikembalikan.
            </li>
          )}
          <li>Riwayat transaksi tetap kami simpan sebagai catatan keuangan.</li>
          <li>Email Anda bisa dipakai mendaftar lagi, tapi data lama tidak ikut kembali.</li>
        </ul>
        <div className="field">
          <label htmlFor="del-password">Password</label>
          <input
            id="del-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="del-confirm">
            Ketik <code>{CONFIRM_WORD}</code> untuk konfirmasi
          </label>
          <input id="del-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" required />
        </div>
        <div className="settings-modal-foot">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button type="submit" className="btn btn-danger" disabled={!ready || busy}>
            {busy && <span className="spinner" />}
            Hapus akun permanen
          </button>
        </div>
      </form>
    </Modal>
  );
}
